import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getUser, logout, isAdmin } from '../lib/auth.jsx';

// Responsive nav:
//   ≥ md (768px): full inline button row (desktop behavior unchanged)
//   < md         : logo + hamburger; menu drops down as a full-width panel
export default function Navbar() {
  const nav = useNavigate();
  const loc = useLocation();
  const u = getUser();
  const [open, setOpen] = useState(false);

  // Close mobile menu whenever the route changes
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const doLogout = () => { logout(); setOpen(false); nav('/'); };

  const SignedOutLinks = ({ mobile = false }) => (
    <>
      <a href="https://qmd.atlasgeo.ai/" target="_blank" rel="noopener noreferrer" className={mobile ? "btn btn-ghost w-full justify-start" : "btn btn-ghost"}>Demo</a>
      <a href="#pricing" className={mobile ? "btn btn-ghost w-full justify-start" : "btn btn-ghost"}>Pricing</a>
      <a href="#how" className={mobile ? "btn btn-ghost w-full justify-start" : "btn btn-ghost"}>How it works</a>
      <Link to="/login" className={mobile ? "btn btn-primary w-full justify-center" : "btn btn-primary"}>Sign in</Link>
    </>
  );

  const SignedInLinks = ({ mobile = false }) => (
    <>
      <a href="https://qmd.atlasgeo.ai/" target="_blank" rel="noopener noreferrer" className={mobile ? "btn btn-ghost w-full justify-start" : "btn btn-ghost"}>Demo</a>
      <Link to="/dashboard" className={mobile ? "btn btn-ghost w-full justify-start" : "btn btn-ghost"}>Dashboard</Link>
      <Link to="/settings" className={mobile ? "btn btn-ghost w-full justify-start" : "btn btn-ghost"}>Settings</Link>
      {isAdmin() && <Link to="/admin" className={mobile ? "btn btn-ghost w-full justify-start" : "btn btn-ghost"}>Admin</Link>}
      <Link to="/jobs/new" className={mobile ? "btn btn-gold w-full justify-center" : "btn btn-gold"}>+ New Job</Link>
    </>
  );

  return (
    <header className="border-b border-slate-200 bg-white sticky top-0 z-30">
      <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 min-w-0">
          <img src="/atlasgeo-symbol.png" alt="Atlas GEO Technologies" className="h-9 w-9 md:h-10 md:w-10 object-contain flex-shrink-0" />
          <div className="flex flex-col min-w-0">
            <span className="text-navy font-bold text-sm md:text-base leading-none tracking-tight truncate">MAPTRIUM™</span>
            <span className="text-[10px] md:text-[11px] text-gold font-semibold uppercase tracking-[0.18em] mt-0.5 hidden sm:inline">by Atlas GEO Technologies</span>
          </div>
        </Link>

        {/* Desktop nav: unchanged */}
        <nav className="hidden md:flex items-center gap-2 text-sm">
          {!u && <SignedOutLinks />}
          {u && (
            <>
              <SignedInLinks />
              <div className="ml-2 flex items-center gap-2 pl-3 border-l border-slate-200">
                <div className="text-xs">
                  <div className="font-semibold text-ink leading-tight">{u.name || u.email}</div>
                  <div className="text-ink-muted leading-tight capitalize">{u.tier || 'starter'} · {u.role}</div>
                </div>
                <button className="btn btn-sm btn-ghost" onClick={doLogout}>Sign out</button>
              </div>
            </>
          )}
        </nav>

        {/* Mobile hamburger */}
        <button
          type="button"
          className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-md hover:bg-navy/5 text-navy"
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          onClick={() => setOpen(o => !o)}
        >
          {open ? (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M6 6l12 12M6 18L18 6" /></svg>
          ) : (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M4 7h16M4 12h16M4 17h16" /></svg>
          )}
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white shadow-lg">
          <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col gap-2">
            {u && (
              <div className="px-3 py-2 bg-slate-50 rounded-md">
                <div className="font-semibold text-ink text-sm truncate">{u.name || u.email}</div>
                <div className="text-xs text-ink-muted capitalize">{u.tier || 'starter'} · {u.role}</div>
              </div>
            )}
            {!u && <SignedOutLinks mobile />}
            {u && <SignedInLinks mobile />}
            {u && (
              <button className="btn btn-ghost w-full justify-start text-red-700" onClick={doLogout}>Sign out</button>
            )}
          </div>
        </div>
      )}

      <div className="gold-stripe" />
    </header>
  );
}
